import {Injectable} from '@angular/core';
import {RIGHTS_PAGE_GOLDEN_SOURCE} from '@entities/rights/constants';
import {RightsFranchiseRequest, RightsInventoryRequest} from '@entities/rights/rights-api-interfaces';
import {
	ContentCode,
	Channel,
	InventoryWithPagination,
	InventoryXLS,
	Language,
	MediaType,
	ProductCode,
	ProjectCode,
	RightsSourcesOption,
	Territory,
	WorkflowStatus,
	RightsLanguageMetadata,
	Genre,
} from '@entities/rights/rights.interfaces';
import {ApiService} from 'amc-common-components';
import {Observable, of} from 'rxjs';
import {map} from 'rxjs/operators';
import {RightsApiMapper} from './rights.api.mapper';

/**
 * This class is an injectable Angular service that provides a way to fetch data..
 */
@Injectable()
export class RightsApiService {
	/**
	 * Constructor for RightsApiService.
	 */
	constructor(private readonly api: ApiService) {}

	/**
	 * API method Get Rights Sources.
	 * @returns {Observable<RightsSourcesOption[]>} - An observable that emits the rights sources.
	 */
	getRightsSources(): Observable<RightsSourcesOption[]> {
		return of(RIGHTS_PAGE_GOLDEN_SOURCE);
	}

	/**
	 * API method Get Inventory for rights/get_rightreport_windows response.
	 * @param {RightsInventoryRequest} params - param for inventory.
	 * @returns {Observable<InventoryWithPagination>} - An observable that emits the inventory.
	 */
	getInventory(params: RightsInventoryRequest): Observable<InventoryWithPagination> {
		return this.api.post('rights/get_rightreport_windows', params, RightsApiMapper.mapInventory.bind(null, params));
	}

	/**
	 * API method Get Languages Metadata for rights/get_rightreport_content_languages_metadata response.
	 * @param {RightsFranchiseRequest} params
	 * @returns {Observable<Record<string, RightsLanguageMetadata[]>>}
	 */
	getLanguagesMetadata(params: RightsFranchiseRequest): Observable<Record<string, RightsLanguageMetadata[]>> {
		return this.api
			.post('rights/get_rightreport_content_languages_metadata', params)
			.pipe(map(RightsApiMapper.mapLanguagesMetadata));
	}

	/**
	 * API method Get Languages Metadata for rights/get_channels response.
	 * @param {RightsFranchiseRequest} params
	 * @returns {Observable<Channel[]>}
	 */
	getChannels(params: RightsFranchiseRequest): Observable<Channel[]> {
		return this.api.post('rights/get_channels', params).pipe(map(RightsApiMapper.mapChannels));
	}

	/**
	 * API method Get Languages Metadata for rights/get_genres response.
	 * @param {RightsFranchiseRequest} params
	 * @returns {Observable<Genre[]>}
	 */
	getGenres(params: RightsFranchiseRequest): Observable<Genre[]> {
		return this.api.post('rights/get_genres', params).pipe(map(RightsApiMapper.mapGenres));
	}

	/**
	 * API method Get X L S Inventory for rights/get_rightreport_windows response.
	 * @param {RightsInventoryRequest} params - param for x l s inventory.
	 * @returns {Observable<InventoryXLS[]>} - An observable that emits the x l s inventory.
	 */
	/* used before universal report api */
	getXLSInventory(params: RightsInventoryRequest): Observable<InventoryXLS[]> {
		return this.api
			.post('rights/get_rightreport_windows', params, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapInventoryXLS));
	}

	/**
	 * API method Get Languages for rights/get_languages response.
	 * @param {RightsFranchiseRequest} franchise - param for languages.
	 * @returns {Observable<Language[]>} - An observable that emits the languages.
	 */
	getLanguages(franchise: RightsFranchiseRequest): Observable<Language[]> {
		return this.api
			.post('rights/get_languages', franchise, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapLanguages));
	}

	/**
	 * API method Get Territories for rights/get_territories response.
	 * @param {RightsFranchiseRequest} franchise - param for territories.
	 * @returns {Observable<Territory[]>} - An observable that emits the territories.
	 */
	getTerritories(franchise: RightsFranchiseRequest): Observable<Territory[]> {
		return this.api
			.post('rights/get_territories', franchise, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapTerritories));
	}

	/**
	 * API method Get Media Types for rights/get_media response.
	 * @param {RightsFranchiseRequest} franchise - param for media types.
	 * @returns {Observable<MediaType[]>} - An observable that emits the media types.
	 */
	getMediaTypes(franchise: RightsFranchiseRequest): Observable<MediaType[]> {
		return this.api
			.post('rights/get_media', franchise, ApiService.NoCache)
			.pipe(map(RightsApiMapper.mapMediaTypes));
	}

	/**
	 * API method Get Brands for rights/get_brands response.
	 * @param {RightsFranchiseRequest} franchise - param for brands.
	 * @returns {Observable<ContentCode[]>} - An observable that emits the brands.
	 */
	getBrands(franchise: RightsFranchiseRequest): Observable<ContentCode[]> {
		return this.api
			.post('rights/get_brands', franchise, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapBrands));
	}

	/**
	 * API method Get Project Types for rights/get_project_types response.
	 * @param {RightsFranchiseRequest} franchise - param for project types.
	 * @returns {Observable<ProjectCode[]>} - An observable that emits the project types.
	 */
	getProjectTypes(franchise: RightsFranchiseRequest): Observable<ProjectCode[]> {
		return this.api
			.post('rights/get_project_types', franchise, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapProjectTypes));
	}

	/**
	 * API method Get Product Owners for rights/get_product_owners response.
	 * @param {RightsFranchiseRequest} franchise - param for product owners.
	 * @returns {Observable<ProductCode[]>} - An observable that emits the product owners.
	 */
	getProductOwners(franchise: RightsFranchiseRequest): Observable<ProductCode[]> {
		return this.api
			.post('rights/get_product_owners', franchise, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapProductOwners));
	}

	/**
	 * API method Get Workflow Statuses for rights/get_workflow_statuses response.
	 * @returns {Observable<WorkflowStatus[]>} - An observable that emits the workflow statuses.
	 */
	getWorkflowStatuses(): Observable<WorkflowStatus[]> {
		return this.api
			.post('rights/get_workflow_statuses', {}, {cacheExpirationTime: 5 * 60 * 1000})
			.pipe(map(RightsApiMapper.mapWorkflowStatuses));
	}
}
